// StudioDesk — the focus view over the Studio desk (pen "Studio – the desk:
// open, lift, close"; #161). While something is in focus the desk is lightly
// dimmed and frozen: the dimming takes the pointer and the wheel, so no
// drag, scroll or pinch moves or zooms the canvas behind, and a click on it
// ends focus. The chrome (title, toolbars) is drawn after this layer, so it
// stays above.
//
// One view, whatever the depth: only the level shown (the last of the path)
// is drawn, with the levels above it as its breadcrumb. A binder or shelf is
// a DeskPanel, lifted over the spot of the card the path starts from; a
// document a DocumentFrame in the middle of the view, whose middle the host
// renders (`renderDocument`: the editor, a source's original, a file's
// facts). Pure: the path (studioDesk.ts) and the cards are projected in;
// every change goes out as desk commands or a part intent. Briefs (plan,
// revise, generate, the readiness foot) come in as `briefs` (deskFacts.ts,
// DeskBriefs.tsx).

export { StudioDesk };

import { exhaustSwitchCase } from "@bilda/utils";
import { type ReactNode, useEffect, useRef } from "react";
import type { PartIntent } from "../ModuleCard/partOps.js";
import type { ContainerCard, StudioCard } from "../Studio/studioCards.js";
import {
    type DeskCommand,
    type DeskDocument,
    type DeskState,
    DOCUMENT_FRAME,
    deskBinder,
    deskDocument,
    inFocus,
    openPartCommands,
} from "../Studio/studioDesk.js";
import type {
    Placement,
    Point,
    Size,
    StudioView,
} from "../Studio/studioLayout.js";
import type { DeskCrumb } from "./DeskCrumbs.js";
import { DeskPanel } from "./DeskPanel.js";
import { DocumentFrame } from "./DocumentFrame.js";
import type { DeskBriefs } from "./deskFacts.js";
import "./StudioDesk.css";

// The lifted panel's width (pen: 620).
const PANEL_WIDTH = 620;
// Kept clear of the viewport's edges.
const EDGE = 16;
// Clear of the Studio toolbar on the left (pen "Projects and Studio",
// decision 1) and below the Studio's title pill.
const TOOLBAR_CLEAR = 96;
const TITLE_CLEAR = 72;

// The wheel over the focus view: a pinch (ctrl/⌘) never zooms, and over
// the dimmed desk nothing scrolls; inside the view its own content scrolls.
const freezeWheel = (e: WheelEvent) => {
    const target = e.target;
    const inView =
        target instanceof Element &&
        target.closest(".bilda-desk__panel") !== null;
    if (e.ctrlKey || e.metaKey || !inView) e.preventDefault();
};

const StudioDesk = ({
    desk,
    cards,
    placements,
    view,
    size,
    project,
    online,
    onDesk,
    onPart,
    onExport,
    renderDocument,
    briefs,
}: {
    readonly desk: DeskState;
    readonly cards: readonly StudioCard[];
    readonly placements: ReadonlyMap<string, Placement>;
    readonly view: StudioView;
    // The viewport's size: the focus view stays inside it.
    readonly size: Size;
    // The project's name: the breadcrumb's first step.
    readonly project: string;
    readonly online: boolean;
    readonly onDesk: (...commands: readonly DeskCommand[]) => void;
    // A part moved inside the container shown.
    readonly onPart: (container: ContainerCard, intent: PartIntent) => void;
    readonly onExport: (card: ContainerCard) => void;
    // The middle of the open document (the one being edited).
    readonly renderDocument: (doc: DeskDocument, front: boolean) => ReactNode;
    readonly briefs: DeskBriefs;
}): ReactNode => {
    const layer = useRef<HTMLDivElement>(null);
    const shown = inFocus(desk);
    // React's onWheel is passive and could not keep a pinch from zooming
    // the page; this listener is not.
    const focused = shown !== undefined;
    // biome-ignore lint/correctness/useExhaustiveDependencies: the layer exists only while something is in focus; it re-subscribes when it appears.
    useEffect(() => {
        const el = layer.current;
        if (el === null) return;
        el.addEventListener("wheel", freezeWheel, { passive: false });
        return () => el.removeEventListener("wheel", freezeWheel);
    }, [focused]);
    if (shown === undefined) return null;

    const { zoom, pan } = view;
    const { path } = desk;
    const titleOf = (id: string): string =>
        deskBinder(cards, id)?.title ?? deskDocument(cards, id)?.title ?? "";
    const crumbs: readonly DeskCrumb[] = path
        .slice(0, -1)
        .map((e) => ({ id: e.id, title: titleOf(e.id) }));
    const intents = {
        onClose: () => onDesk({ type: "close", id: shown.id }),
        onCrumb: (id: string | null) =>
            onDesk(
                id === null
                    ? { type: "desk_clicked" }
                    : { type: "close_to", id },
            ),
    };
    // The panel lies over the spot of the card the path starts from (it
    // stays put as the path deepens); clear of the chrome and the edges.
    const spot = placements.get(path[0]?.id ?? "");
    const panelAt: Point = {
        x: Math.max(
            TOOLBAR_CLEAR,
            Math.min(
                pan.x + (spot?.x ?? 0) * zoom,
                size.width - PANEL_WIDTH - EDGE,
            ),
        ),
        y: Math.max(TITLE_CLEAR, pan.y + (spot?.y ?? 0) * zoom),
    };
    // A document's frame: its lifted size, or what the view leaves, in the
    // middle of the room right of the toolbar.
    const frame: Size = {
        width: Math.min(
            DOCUMENT_FRAME.width,
            size.width - TOOLBAR_CLEAR - EDGE,
        ),
        height: Math.min(
            DOCUMENT_FRAME.height,
            size.height - TITLE_CLEAR - EDGE,
        ),
    };
    const frameAt: Point = {
        x: Math.max(TOOLBAR_CLEAR, (size.width - frame.width) / 2),
        y: Math.max(TITLE_CLEAR, (size.height - frame.height) / 2),
    };

    const content = ((): ReactNode => {
        switch (shown.kind) {
            case "binder":
            case "shelf": {
                const card = deskBinder(cards, shown.id);
                if (card === undefined) return null;
                const openPart = (part: string) =>
                    onDesk(...openPartCommands(card, part));
                return (
                    <DeskPanel
                        key={shown.id}
                        entry={shown}
                        card={card}
                        project={project}
                        crumbs={crumbs}
                        at={panelAt}
                        liftFrom={
                            spot === undefined || path.length > 1
                                ? undefined
                                : Math.min(1, (spot.width * zoom) / PANEL_WIDTH)
                        }
                        online={online}
                        readiness={briefs.readiness.get(shown.id)}
                        note={briefs.notes.get(shown.id)}
                        intents={{
                            ...intents,
                            onPlanDocument: (title, brief) =>
                                briefs.onPlanDocument(shown.id, title, brief),
                            onPlanModule: (title) =>
                                briefs.onPlanModule(shown.id, title),
                            onGenerateAll: (ids) =>
                                briefs.onGenerateAll(shown.id, ids),
                            onPart: (intent) =>
                                intent.type === "open"
                                    ? openPart(intent.part)
                                    : onPart(card, intent),
                            onOpenModule: openPart,
                            onExport: () => onExport(card),
                        }}
                    />
                );
            }
            case "document": {
                const doc = deskDocument(cards, shown.id);
                if (doc === undefined) return null;
                // The structure beside it: where the path starts.
                const first = path[0];
                const root =
                    first === undefined || first.kind === "document"
                        ? null
                        : (deskBinder(cards, first.id) ?? null);
                return (
                    <DocumentFrame
                        key={shown.id}
                        doc={doc}
                        root={root}
                        project={project}
                        crumbs={crumbs}
                        at={frameAt}
                        size={frame}
                        online={online}
                        note={briefs.notes.get(shown.id)}
                        middle={renderDocument(doc, true)}
                        intents={{
                            ...intents,
                            onRevise: (brief) =>
                                briefs.onReviseBrief(shown.id, brief),
                            onGenerate: () => briefs.onGenerate(shown.id),
                            onOpen: (id) =>
                                root === null
                                    ? undefined
                                    : onDesk(...openPartCommands(root, id)),
                        }}
                    />
                );
            }
            default:
                return exhaustSwitchCase(shown.kind);
        }
    })();
    return (
        <div ref={layer} className="bilda-desk">
            {/* The dimmed desk: a click outside the focus view ends focus
                (Esc and the breadcrumb do the same). */}
            <div
                className="bilda-desk__dim"
                aria-hidden="true"
                onPointerDown={(e) => {
                    if (e.button === 0) onDesk({ type: "desk_clicked" });
                }}
            />
            {content}
        </div>
    );
};
